import { glasbandOben } from './geraet'

/**
 * How high the band is that iOS 27 paints over the top of a home-screen
 * web app (#48, 1.53.1) -- in points, **one place**.
 *
 * **Measured, not estimated** (1.54.1). It stood at 44 and was too much
 * ("zu viel Luft", from use). Two screenshots of the same iPhone, one with
 * the margin switched off and one with it on, read out pixel by pixel and
 * divided by 3:
 *
 * ```
 *                                          ohne Rand   mit Rand
 * Fenster beginnt unter der Statusleiste        59         59
 * Band verklungen (Grau erreicht Weiss)         84         89
 * Titel oben                                    79        111
 * dunkelstes Pixel im Titel                     19          0
 * ```
 *
 * So the band reaches about **30 points** into the content, and a title
 * standing in it is measurably pale -- 19 instead of 0 is the finding of
 * #48 as a number. 32 is two points above that, because the lower edge is
 * a gradient and not a line; the title starts some 9 points below the
 * header's padding and thus stands free.
 *
 * Chromium paints no band, so no run can judge this number. It is read off
 * the device instead: the card *Stand* under Einstellungen > Diagnose
 * prints it next to what the header really keeps free.
 */
export const GLASBAND_PX = 32

/**
 * The switch per device (1.53.3), in `localStorage` like every preference
 * (`lib/preference.ts`): `'false'` turns the margin off, anything else
 * leaves it on. From use: "ich bin unsicher, kann das aber nicht testen,
 * weil die Screens jetzt so weit unten beginnen" -- whether iOS 27 still
 * paints its band once the app is added to the home screen anew can only
 * be seen with the margin off, and that is a question for the device, not
 * for Chromium. The default stays on: the margin is the answer to a
 * picture, the switch is the way to take the next one.
 */
export const GLASBAND_SCHLUESSEL = 'tb.glasband'

/** Whether the margin is wanted on this device -- on unless switched off. */
export function glasbandGewuenscht(): boolean {
  try {
    return localStorage.getItem(GLASBAND_SCHLUESSEL) !== 'false'
  } catch {
    return true
  }
}

/**
 * Keep the band free -- once, at startup, before the first paint.
 *
 * Sets `--sicherheit-oben` on the root when `glasbandOben()` says so and
 * the device has not switched it off; the headers add it to the safe-area
 * inset, `max(10px, calc(env(safe-area-inset-top) + var(--sicherheit-oben)))`
 * (`.bar`, `.buch__leiste`, `.lightbox__kopf`, `.stand-wache`, the push
 * banner, the full sheet's strip). Where the variable is not set, nothing
 * changes -- the same shape as `--sicherheit-unten`.
 *
 * **Added, not compared** (1.131.0). The band lies *below* the status bar.
 * Since #48 took `apple-mobile-web-app-status-bar-style: black-translucent`
 * out, the window normally begins below the status bar and
 * `env(safe-area-inset-top)` is 0 (iPhone 16 Pro: `Sicherheitsabstände:
 * 0 / 0 / 34 / 0`), so `max()` and the sum gave the same and the difference
 * never showed. After Apple's full-screen video player the window covers
 * the status bar again and iOS reports it (32 on the iPad, 59 on the
 * iPhone); `max()` then swallowed the band and every header stood 32
 * points too high, until the app went to the background and came back.
 * 1.130.0 read that as a forgotten inset and put one back -- the log it
 * added showed the inset present and the sum missing.
 */
export function glasbandAnwenden(): void {
  obenSetzen()
}

/** The one place that writes `--sicherheit-oben`: the band, or nothing. */
function obenSetzen(gewuenscht = glasbandGewuenscht()): void {
  if (glasbandOben() && gewuenscht) document.documentElement.style.setProperty('--sicherheit-oben', `${GLASBAND_PX}px`)
  else document.documentElement.style.removeProperty('--sicherheit-oben')
}

/**
 * The switch, applied at once: remembered for the next start and set or
 * taken off the root right away, so that the header moves under the finger
 * that flipped it (the same shape as `setzeGroesse` in `lib/darstellung.ts`).
 */
export function glasbandSetzen(an: boolean): void {
  try {
    localStorage.setItem(GLASBAND_SCHLUESSEL, JSON.stringify(an))
  } catch {
    // Not being able to remember is no reason not to obey now.
  }
  obenSetzen(an)
}

/**
 * What is kept free at the top, right now -- for the card *Stand*.
 *
 * **Two numbers, because one of them says nothing on its own.** `rand` is
 * what this module has set; `kopfzeile` is what the header really keeps
 * free, and that is the device's inset plus the margin (at least the
 * padding). As long as both are equal, the device reports no inset; does
 * the header keep more, the window covers the status bar (after Apple's
 * full-screen player) and the difference is that bar. Printing only "ja"
 * hid that; printing both makes it readable off the device, which is where
 * this number is judged.
 *
 * Measured on the visible header, because a phone keeps the list's header
 * standing hidden beside the page's.
 */
export function glasbandStand(): { rand: number; kopfzeile: number } {
  const gesetzt = parseFloat(document.documentElement.style.getPropertyValue('--sicherheit-oben'))
  const leiste = [...document.querySelectorAll('.bar')].find((b) => b.getClientRects().length > 0)
  const polster = leiste === undefined ? NaN : parseFloat(getComputedStyle(leiste).paddingTop)
  return {
    rand: Number.isFinite(gesetzt) ? Math.round(gesetzt) : 0,
    kopfzeile: Number.isFinite(polster) ? Math.round(polster) : 0,
  }
}
